import { useCallback, useEffect, useState } from 'react';
import { bugReportClient } from '../../lib/connect/clients';
/** Proto timestamps are seconds+nanos; the table wants an ISO string. */ function timestampToISO(ts) {
    return new Date(Number(ts.seconds) * 1000 + ts.nanos / 1000000).toISOString();
}
/**
 * Hook to fetch bug reports for the authenticated user
 */ export function useBugReports(options = {}) {
    const { limit = 50, skip = 0, status, autoFetch = true } = options;
    const [bugReports, setBugReports] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchBugReports = useCallback(async ()=>{
        setLoading(true);
        setError(null);
        try {
            // Jira is the record now, so this lists issues rather than rows. skip is
            // translated to a page number because JQL pages by offset and the RPC
            // takes a page — same window, stated the way the server thinks about it.
            const response = await bugReportClient.listBugReports({
                page: Math.floor(skip / limit) + 1,
                pageSize: limit,
                status: status ?? ''
            });
            setBugReports(response.reports.map((r)=>({
                    // The issue key IS the identifier now; there is no second id to
                    // carry since the Mongo row it came from is gone.
                    bugReportID: r.jiraTicketNumber,
                    jiraTicketNumber: r.jiraTicketNumber,
                    jiraUrl: r.jiraUrl,
                    title: r.title,
                    description: r.description,
                    status: r.status,
                    assignee: r.assignee || undefined,
                    // Page and browser context live in the issue description now, since
                    // Jira has nowhere structured to put them.
                    pageUrl: '',
                    browserInfo: '',
                    createdAt: r.createdAt ? timestampToISO(r.createdAt) : '',
                    updatedAt: r.createdAt ? timestampToISO(r.createdAt) : ''
                })));
            setTotal(response.total);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(errorMessage);
            console.error('Error fetching bug reports:', err);
        } finally{
            setLoading(false);
        }
    }, [
        limit,
        skip,
        status
    ]);
    useEffect(()=>{
        if (autoFetch) {
            fetchBugReports();
        }
    }, [
        autoFetch,
        fetchBugReports
    ]);
    return {
        bugReports,
        total,
        loading,
        error,
        refetch: fetchBugReports
    };
}
